import PropTypes from 'prop-types';
import { useBoolean, useLocalStorage } from 'usehooks-ts';
import { Stack } from '@mui/material';

import { HEADER_HEIGHT } from 'constants/components';
import { LOCAL_STORAGE_KEY } from 'constants/localStorage';
import { useHeaderHeight } from 'hooks/useHeaderHeight';
import PageContainer from 'layouts/PageContainer';
import FullScreenLoader from 'layouts/FullScreenLoader';
import { ButtonBack, MaintenanceWarning, PageBreadcrumbs } from 'components/simple';

const styles = {
  container: ({ breakpoints }, withNavigation) => ({
    minHeight: '100vh',
    textAlign: 'left',
    paddingBottom: 8,
    paddingInline: 3,
    paddingTop: withNavigation ? `${HEADER_HEIGHT + 51}px` : `${HEADER_HEIGHT + 28}px`,
    [breakpoints.up('md')]: {
      paddingTop: withNavigation ? `${HEADER_HEIGHT + 30}px` : `${HEADER_HEIGHT + 150}px`,
    },
  }),
  containerWithMaintenanceWarning: ({ breakpoints }, headerHeight) => ({
    paddingTop: `${headerHeight}px`,
    [breakpoints.up('md')]: {
      paddingTop: `${headerHeight}px`,
    },
  }),
};

const PageWrapper = ({
  maxWidth = undefined,
  title = null,
  back = null,
  children = null,
  breadcrumbs = [],
  isLoading = false,
  customStyles = null,
}) => {
  const [hideMaintenanceWarning, setHideMaintenanceWarning] = useLocalStorage(
    LOCAL_STORAGE_KEY.HIDE_MAINTENANCE_WARNING,
    false,
  );
  const { value: showMaintenanceWarning, setFalse: setShowMaintenanceWarningFalse } =
    useBoolean(hideMaintenanceWarning);
  const withNavigation = !!back || !!breadcrumbs.length;

  const { headerHeight } = useHeaderHeight();

  const onCloseMaintenanceWarning = () => {
    setShowMaintenanceWarningFalse();
    setHideMaintenanceWarning(true);
  };

  if (isLoading) {
    return <FullScreenLoader />;
  }

  return (
    <PageContainer
      maxWidth={maxWidth}
      sx={(theme) => ({
        ...styles.container(theme, withNavigation),
        ...(showMaintenanceWarning && styles.containerWithMaintenanceWarning(theme, headerHeight)),
        ...customStyles,
      })}
    >
      {showMaintenanceWarning && <MaintenanceWarning onCloseMaintenanceWarning={onCloseMaintenanceWarning} />}

      {withNavigation && (
        <Stack
          className="remove_for_pdf"
          mb={{ xs: 4, sm: 7 }}
          gap={{ xs: 2, lg: 5 }}
          alignItems={{ xs: 'flex-start', lg: 'center' }}
          direction={{ xs: 'column', lg: 'row' }}
        >
          {!!back && <ButtonBack back={back} />}

          {!!breadcrumbs.length && <PageBreadcrumbs breadcrumbs={breadcrumbs} />}
        </Stack>
      )}

      {title}

      <div>{children}</div>
    </PageContainer>
  );
};

PageWrapper.propTypes = {
  maxWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  children: PropTypes.node,
  title: PropTypes.node,
  back: PropTypes.shape({
    to: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.objectOf(PropTypes.oneOfType([PropTypes.string, PropTypes.objectOf(PropTypes.string)])),
    ]),
    disabled: PropTypes.bool,
  }),
  breadcrumbs: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      link: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.objectOf(PropTypes.oneOfType([PropTypes.string, PropTypes.objectOf(PropTypes.string)])),
      ]),
    }),
  ),
  isLoading: PropTypes.bool,
  customStyles: PropTypes.shape({}),
};
export default PageWrapper;
